<template>
  <div class="container-fluid page-container">
    <ban-edit-profile v-if="isBannedFromCoverage" />
    <template v-else-if="coverageList">
      <div class="row page-header__container mb-2 mb-md-4 pb-3">
        <div class="col-12 col-md-5">
          <page-heading :class="'align-items-center'" :title="pageTitle" />
        </div>
        <div class="page-filter-control col-12 col-md-7 d-md-flex justify-content-end">
          <coverage-control
            :class="'row justify-content-end align-self-center'"
            :current="selected"
            @rename="renameCoverage"
          >
            <template v-if="isMobile || isTablet" v-slot:before-filter-icon>
              <div class="col col-12">
                <div class="row justify-content-end coverage-control coverage-control-mobile">
                  <coverage-dropdown :coverage-area-options="coverageAreaOptions" />
                </div>
              </div>
            </template>
            <template v-slot:contextual>
              <div class="col col-12">
                <div class="row justify-content-end coverage-control">
                  <coverage-dropdown v-if="isDesktop" :coverage-area-options="coverageAreaOptions" />
                  <div class="separator update-coverage d-flex flex-column flex-xl-row">
                    <b-button
                      size="sm"
                      class="mr-xl-3 mb-3 mb-xl-0 text-nowrap"
                      :variant="'outline-secondary'"
                      :class="[{disabled:!selected}]"
                      :disabled="selected === null"
                      @click="renameCoverage()"
                    >
                      <font-awesome-icon :icon="'edit'" />
                      {{ $t('Rename') }}
                    </b-button>
                    <b-button
                      size="sm"
                      class="text-nowrap align-items-center"
                      :variant="'outline-secondary'"
                      :class="[{disabled:!selected}]"
                      :disabled="selected === null"
                      @click="$bvModal.show('coverage-delete-confirmation')"
                    >
                      <span
                        v-b-tooltip.hover
                        class="d-flex justify-content-center align-items-center w-100 stretched-link"
                        :title="selected === null ? 'You have no coverage to delete': ''"
                      ><font-awesome-icon class="text-danger mr-1" :icon="'times-circle'" />{{ $t('Delete') }}</span>
                    </b-button>
                  </div>
                  <div class="separator d-flex new-coverage">
                    <b-button
                      size="sm"
                      :variant="'outline-secondary'"
                      :class="['text-nowrap', 'mr-xl-1']"
                      @click="newCoverage()"
                    >
                      <font-awesome-icon class="text-success" :icon="['fas', 'plus-circle']" />
                      {{ newCoverageText }}
                    </b-button>
                  </div>
                </div>
              </div>
            </template>
          </coverage-control>
        </div>
      </div>
      <div v-if="selected !== null" class="row">
        <div class="col-12 d-flex justify-content-xxl-between">
          <b-button-group class="coverage-steps">
            <a
              :class="['h5', 'font-weight-bold', 'btn-light', 'coverage-steps__tab', 'first', 'mb-0', {'coverage-steps__tab--selected' : step === 'lead-types'}]"
              @click="switchTab({name:'lead-selection-&-coverage-tab', params: {step: 'lead-types'}})"
            >
              {{ $t('1. Choose your lead types') }}
            </a>
            <a
              :class="['h5', 'font-weight-bold', 'btn-light', 'coverage-steps__tab', 'mb-0', {'coverage-steps__tab--selected' : step === 'accreditations'}]"
              @click="switchTab({name:'lead-selection-&-coverage-tab', params: {step: 'accreditations'}})"
            >
              {{ $t('2. Add your accreditations') }}
            </a>
            <a
              :class="['h5', 'font-weight-bold', 'btn-light', 'coverage-steps__tab', 'last', 'mb-0', {'coverage-steps__tab--selected': step === 'postcodes'}]"
              @click="switchTab({name:'lead-selection-&-coverage-tab', params: {step: 'postcodes'}})"
            >
              {{ $t('3. Select coverage') }}
            </a>
          </b-button-group>
          <coverage-buttons
            :back="['accreditations', 'postcodes'].includes(step)"
            :next="['lead-types', 'accreditations'].includes(step)"
            :disable-basket="(!isDesktop && step === 'accreditations' || !isMobile && step === 'lead-types')"
            :disable-next="step === 'postcodes'"
            :disable-save="clean"
            :secondary-button-text="!isDesktop ? (showBasket ? 'Hide Selected' : 'Show Selected') : ''"
            :saving-animation="isSaving"
            :class="['col-md', 'd-md-block', 'nav__sticky-footer','coverage-control-bar', {'control-bar': !isDesktop}]"
            @save="saveTab"
            @back="back"
            @next="next"
            @secondary-button-click="showBasket = !showBasket"
          />
        </div>
      </div>
      <div class="row my-2">
        <div class="col">
          <p class="d-none d-md-block mb-0 py-3 small">
            <font-awesome-icon icon="lightbulb" class="text-warning mr-2" />
            {{ $t('refineCoverage') }}
          </p>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <router-view :key="$route.fullPath" ref="rv" @isSaving="setIsSaving" @clean="clean=$event" />
        </div>
      </div>
    </template>
    <spinner v-else :display-loading="true" />
    <b-modal v-if="selected" id="coverage-delete-confirmation" centered @ok="remove">
      <template #modal-header="{ close }">
        <h5 class="modal-title">
          {{ $t('Confirmation') }}
        </h5>
        <b-button class="close" @click.stop="close">
          <font-awesome-icon icon="times" />
        </b-button>
      </template>
      <p class="my-4">
        {{ $t('deleteConfirm',{selectedName: selectedAutoName}) }}
      </p>
      <b-alert variant="warning" :show="isLastCoverage">
        {{ $t('Deleting your last coverage area will disable your company listing!') }}'
      </b-alert>
      <template #modal-footer="{ ok, cancel }">
        <b-button size="sm" variant="outline-secondary" @click="cancel()">
          {{ $t('Cancel') }}
        </b-button>
        <b-button size="sm" variant="primary" @click="ok()">
          {{ $t('OK') }}
        </b-button>
      </template>
    </b-modal>
    <b-modal id="new-coverage" centered @ok.prevent="addNewCoverage">
      <template #modal-header="{ close }">
        <h5 class="modal-title">
          {{ $t('Coverage area') }}
        </h5>
        <b-button class="close" @click.stop="close">
          <font-awesome-icon icon="times" />
        </b-button>
      </template>
      <validation-observer ref="newobserver" v-slot="{ handleSubmit }">
        <b-form @submit.prevent="handleSubmit(addNewCoverage)">
          <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
            <b-form-group :label="$t('Coverage area name')">
              <b-form-input
                v-model.trim="newName"
                type="text"
                :state="(dirty || validated) ? (!!valid) : null"
              />
              <b-form-invalid-feedback
                :state="invalid"
              >
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-form>
      </validation-observer>
      <template #modal-footer="{ ok, cancel }">
        <b-button size="sm" variant="outline-secondary" @click="cancel()">
          {{ $t('Cancel') }}
        </b-button>
        <b-button size="sm" variant="primary" @click="ok()">
          {{ $t('OK') }}
        </b-button>
      </template>
    </b-modal>
    <b-modal id="edit-coverage" centered @ok="save">
      <template #modal-header="{ close }">
        <h5 class="modal-title">
          {{ $t('Coverage area') }}
        </h5>
        <b-button class="close" @click.stop="close">
          <font-awesome-icon icon="times" />
        </b-button>
      </template>
      <validation-observer ref="observer" v-slot="{ handleSubmit }">
        <b-form @submit.prevent="handleSubmit(save)">
          <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
            <b-form-group :label="$t('Coverage area name')">
              <b-form-input v-model.trim="editName" type="text" :state="(dirty || validated) ? (!!valid) : null" />
              <b-form-invalid-feedback
                :state="invalid"
              >
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-form>
      </validation-observer>
      <template #modal-footer="{ ok, cancel }">
        <b-button size="sm" variant="outline-secondary" @click="cancel()">
          {{ $t('Cancel') }}
        </b-button>
        <b-button size="sm" variant="primary" @click="ok()">
          {{ $t('OK') }}
        </b-button>
      </template>
    </b-modal>
    <b-modal id="nav-confirm" centered @ok="discardUnsavedChanges()">
      <template #modal-header="{ close }">
        <h5 class="modal-title">
          {{ $t('Coverage area') }}
        </h5>
        <b-button class="close" @click.stop="close">
          <font-awesome-icon icon="times" />
        </b-button>
      </template>
      <div>{{ $t('You have unsaved changes which will be lost if you navigate away from this tab. Do you want to proceed?') }}</div>
      <template #modal-footer="{ ok, cancel }">
        <b-button size="sm" variant="outline-secondary" @click="cancel()">
          {{ $t('No') }}
        </b-button>
        <b-button size="sm" variant="primary" @click="ok()">
          {{ $t('Yes') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import PageHeading from "@/components/control/PageHeading";
import Spinner from "@/components/animation/Spinner";
import {COVERAGE_LIST, SELECTED_COVERAGE} from "@/store/coverage/mutation-types";
import Notification from '@/imports/mixins/notification';
import {ValidationObserver, ValidationProvider} from 'vee-validate';
import BanEditProfile from "@/components/control/BanEditProfile";
import CoverageControl from '@/components/control/CoverageControl';
import store from "@/store";
import CoverageButtons from "@/components/control/CoverageButtons";
import CoverageDropdown from "@/components/control/CoverageDropdown";
import userDevice from "@/imports/mixins/userDevice";

export default {
  name: "Coverage",
  components: {
    CoverageButtons,
    CoverageDropdown,
    Spinner, PageHeading, ValidationProvider, ValidationObserver, BanEditProfile, CoverageControl},
  mixins:[Notification, userDevice],
  data: function () {
    return {
      completed: [],
      newName: '',
      clean: true,
      tmpRoute: null,
      saveTarget: null,
      showBasket: false,
      stepOrder: {
        'lead-types': {
          next: 'accreditations',
        },
        'accreditations': {
          next: 'postcodes',
          back: 'lead-types',
        },
        'postcodes': {
          back: 'accreditations'
        }
      },
      isSaving: false,
    }
  },
  computed: {
    coverageList: {
      get: function() {
        return this.$store.state.coverage.COVERAGE_LIST;
      },
      set: function (value) {
        this.$store.commit(COVERAGE_LIST, value)
      }
    },
    isLastCoverage: function() {
      return this.coverageList.length < 2;
    },
    coverageAreaOptions: function(){
      return this.coverageList.map((coverage, key) => ({
        text: coverage.name && (coverage.name !== '' && coverage.name !== '0') ? coverage.name : `Coverage ${key+1}`,
        value:coverage
      }))
    },
    autoNewName: function() {
      const newIndex = this.coverageList.length + 1;
      return  this.$t('CoveragePlusIndex', {indexNumber: newIndex});
    },
    editName: {
      get: function() {
        return this.newName;
      },
      set: function(value) {
        this.newName = value;
      }
    },
    newCoverageText: function() {
      return (this.coverageList.length > 0) ? this.$t('Add New') : this.$t('Add Coverage');
    },
    selectedAutoName: function() {
      const key = (this.coverageList) ? this.coverageList.findIndex(element => element.id === this.selected.id) : 0;
      return this.selected.name && (this.selected.name !== '' && this.selected.name !== '0') ? this.selected.name : this.$t('CoveragePlusIndex', {indexNumber: key+1});
    },
    selected: {
      get: function() {
        return this.$store.state.coverage.SELECTED_COVERAGE;
      },
      set: function (value) {
        this.$store.commit(SELECTED_COVERAGE, value);
      }
    },
    step: function() {
      return (this.$route.params.step) ?? 'lead-types';
    },
    isBannedFromCoverage: function() {
      return this.$store.state.company.COMPANY.ban_edit_coverage;
    },
    pageTitle() {
      return this.$t('Lead Selection & Coverage');
    },
  },
  methods: {
    newCoverage: function() {
      this.selected = null;
      this.newName = this.autoNewName;
      this.$bvModal.show('new-coverage');
    },
    renameCoverage: function () {
      this.newName = this.selectedAutoName;
      this.$bvModal.show('edit-coverage')
    },
    save: function(event) {
      event.preventDefault();
      this.$refs.observer.validate().then(success => {
        if (!success) {
          return;
        }
        this.selected.name = this.newName;
        this.$store.dispatch('saveCoverage', this.selected);
        this.$bvModal.hide('edit-coverage');
      })
    },
    saveNew: function() {
      const coverageObject = {name: this.newName, id: 'tmp'};
      this.$store.dispatch('saveCoverage', coverageObject).then(id => {
        this.selected = this.coverageList.find(coverage => coverage.id === id);
        this.newName = this.autoNewName;
        this.$router.push({name:'lead-selection-&-coverage-tab-0'});
      });
      this.$bvModal.hide('new-coverage');
    },
    addNewCoverage: function() {
      this.$refs.newobserver.validate().then(success => {
        if (!success) {
          return;
        }
        this.saveNew();
      });
    },
    remove: function () {
      const name = this.selectedAutoName;
      this.$store.dispatch('deleteCoverage', this.selected.id).then(() => {
        this.selected = null;
        this.notify(
          this.$bvToast,
          this.$t('coverageDeleted', {coverageName: name}),
          null,
          'success'
        );
      })
    },
    switchTab: function(route) {
      if (this.clean) {
        this.$router.push(route);
        this.tmpRoute = null;
      } else {
        this.tmpRoute = route;
        this.$bvModal.show('nav-confirm');
      }
    },
    saveTab: function() {
      this.$refs.rv.save();
    },
    setIsSaving: function(value) {
      this.isSaving = value;
    },
    next: function() {
      const nextStep = this.stepOrder[this.step].next;
      this.switchTab({name:'lead-selection-&-coverage-tab', params: {step: nextStep}});
    },
    back: function() {
      const prevStep = this.stepOrder[this.step].back;
      this.switchTab({name:'lead-selection-&-coverage-tab', params: {step: prevStep}});
    },
    discardUnsavedChanges: function() {
      this.clean = true;
      this.switchTab(this.tmpRoute);
    }
  },
  watch: {
    showBasket: function() {
      this.$refs.rv.switchBasket();
    }
  },
  created: function() {
    store.dispatch('updateGlobals').then(() => {
      this.$store.dispatch('getCoverage').then(() => {
        if(this.coverageList.length === 0) {
          this.newName = this.$t('CoveragePlusIndex', {indexNumber: 1});
          this.saveNew();
        } else {
          const preSelectId = this.$route.query.listing ?? null;
          const preSelect = this.coverageList.find(coverage => coverage.id === parseInt(preSelectId));
          this.selected = (preSelect !== undefined) ? preSelect : (this.coverageList[0] ?? null);
        }
      });
    });
  }
}
</script>

<style scoped lang="scss">
.coverage-steps {

  @include media-breakpoint-down(lg) {
    width: 100%;
  }

  &__tab {
    box-shadow: inset 0 0 0 1px $border-color;
    cursor: pointer;
    text-align: left;
    text-decoration: none;
    transition: $transition-base;

    @include media-breakpoint-down(sm) {
      padding: 16px 10px;
    }

    @include media-breakpoint-up(md) {
      padding: 16px;
    }

    @include media-breakpoint-between(sm, lg) {
      width: 33.333999%;
    }

    @include media-breakpoint-up(xl) {
      min-width: 180px;
    }

    &:hover {
      background: $theme-secondary-accents-900;
      box-shadow: inset 0 0 0 2px $theme-primary;
    }

    &--selected {
      background-color: $theme-secondary-accents-900;
      box-shadow: inset 0 0 0 2px $theme-primary;
    }

    &.first {
      border-radius: 6px 0 0 6px;
    }

    @include media-breakpoint-down(lg) {

      &.last {
        border-radius: 0 6px 6px 0;
      }
    }
  }
}

@include media-breakpoint-up(xl) {
  .coverage-control-bar {
    border-top: 1px solid;
    border-bottom: 1px solid;
    border-right: 1px solid;
    border-color: $border-color;
    border-radius: 0 6px 6px 0;
    padding-right: 0;
  }
}

.coverage-fieldset {

  @media only screen and (min-width: 1481px) {
    margin-right: .625rem;
    .separator {
      padding-top: 0;
    }
  }

  @media only screen and (min-width: 1200px) and (max-width: 1480px) {
    width: 100%;
    margin-right: 0;
    justify-content: flex-end;
    margin-bottom: 5px;
    .separator {
      padding: 5px 0 10px;

      &:before {
        width: 0;
      }
    }
  }
}

::v-deep .coverage-fieldset {
  .form-row {
    align-items: center;
  }
}

.separator {
  outline: none;
  width: auto;
  flex-direction: column;
  justify-content: center;
  padding: 5px 10px;
  @include media-breakpoint-up(xl) {
    margin-left: 1rem;
  }
}

@include media-breakpoint-up(xl) {
  .new-coverage {
    padding: 5px 0 5px 10px;

    &:before {
      width: 0;
    }
  }

  .update-coverage {
    padding: 5px 10px 5px 0;
    margin-left: 0;

    .btn {
      min-width: 95px;
    }
  }
}

@include media-breakpoint-down(lg) {
  .coverage-control {
    flex-direction: column;
  }

  .coverage-fieldset {
    justify-content: center;
  }

  .separator {
    justify-content: center;
    align-items: center;
    padding: 15px 10px 5px;

    &:before {
      content: none;
    }
    .btn {
      min-width: 125px;
    }
  }

  .new-coverage.separator {
    padding-top: 10px;
  }
}

@include media-breakpoint-down(sm) {
  .coverage-control-mobile {
    max-width: 95%;
    float: right;
  }
}

::v-deep {
  .accordion-toggle {
    &.collapsed {
      transition-duration: .3s;
      transition-property: transform;
    }

    &.not-collapsed {
      svg {
        transition-duration: .3s;
        transition-property: transform;
        transform: rotate(90deg);
      }
    }
  }

  .accordion-items {
    button,
    ul {
      @include font-size($font-size-14);
    }
  }
}

.control-bar {
  bottom: 0;
  left: 0;
  background: $white;
  width: 100%;
  position: fixed;
  box-shadow: $box-shadow;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  @include media-breakpoint-between(md, lg) {
    padding: 10px 30px 10px 0;
  }

  @include media-breakpoint-up(xl) {
    padding: 10px 5px;
  }

  @include media-breakpoint-down(md) {
    z-index: 2;
  }
}

::v-deep .nav__sticky-footer {
  .coverage-control-btn {
    margin-right: 10px;

    @media only screen and (max-width: 374px) {
      min-width: 40px;
    }

    @media only screen and (min-width: 375px) and (max-width: 412px) {
      min-width: 70px;
    }

    @media only screen and (min-width: 413px) and (max-width: 1199px) {
      min-width: 85px;
    }

    @include media-breakpoint-up(xl) {
      min-width: 80px;
    }

    @include media-breakpoint-down(sm) {
      height: 40px;
      line-height: 1.1;
    }
  }

  .animated-submit {
    @media only screen and (max-width: 374px) {
      min-width: 40px;
    }

    @media only screen and (min-width: 375px)  and (max-width: 412px) {
      min-width: 70px;
    }

    @include media-breakpoint-up(lg) {
      min-width: 80px;
    }

    &.animate {
      height: 32px;
      width: 32px;
      margin: 4px auto;
      line-height: 1.1;
      min-width: 32px;

      &.success {
        &:before {
          line-height: 1.5;
        }
      }
    }
  }

  .btn.last {
    margin-right: 0;
  }

  @include media-breakpoint-between(md, lg) {
    .btn.last {
      min-width: 130px;
    }
  }

  @media only screen and (max-width: 374px) {
    .btn.last {
      max-width: 90px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

::v-deep {
  .nav-tabs {
    .nav-link {
      background-color: $white;
      border-radius: 0;
      color: #909BA2;
      font-size: $font-size-15;
      font-weight: $font-weight-bold;
      padding-top: 10px;
      padding-bottom: 10px;

      @include media-breakpoint-between(xl, xxl) {
        width: 275px;
      }

      @include media-breakpoint-up(xxl) {
        width: 290px;
      }

      &[aria-selected="false"] {
        background-color: white;
        box-shadow: inset 0 0 5px 0 $card-header-background;
      }

      &.first-tab {
        border-top-left-radius: 6px;
      }

      &.last-tab {
        border-left: 0;
        border-top-right-radius: 6px;
      }

      &.active {
        color: $theme-primary;
        position: relative;

        &:before {
          background-color: $theme-primary-accents-900;
          content: "\200C";
          height: 5px;
          left: 0;
          position: absolute;
          top: -1px;
          width: 100%;
        }
      }
    }
  }

  .tab-content {
    box-shadow: $box-shadow;

    [role=tabpanel] {
      padding: 0;
    }
  }

  .tab-content,
  .tab-content .tab-pane {
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    overflow: hidden;
  }
}

::v-deep .worktype-placeholder {
  &:before {
    content: "\200C";
    height: 2px;
    background-color: $border-color;
    width: 110px;
    position: absolute;
    bottom: -10px;
    left: 0;
    right: 0;
    margin-left: auto;
    margin-right: auto;
  }
}

@include media-breakpoint-down(sm) {
  .page-filter-control {
    padding-top: 15px;
  }

  ::v-deep .page-header__container {
    &:before {
      content: none;
    }

    .page-header__title {
      padding-bottom: 15px;

      &:before {
        position: absolute;
        content: "\200C";
        height: 1px;
        background-color: $border-color;
        bottom: 0;
        left: 15px;
        right: 15px;
        width: calc(100% - 30px);
      }
    }
  }
}

::v-deep .coverage-control {
  .custom-select-coverage {
    background-color: transparent;
    border: 0;

    &:focus {
      background-color: transparent;
    }
  }
}

::v-deep .accordion-items {
  .custom-control {
    z-index: 0;
  }
}
</style>
